import { BOM_V2_ACTION_TYPES, BomV2ReducerAction, isBomV2Action } from './bom-library-v2.types';
import { BomLibraryV2State, BomLibraryFilterOptions } from '../types';

const EMPTY_BOM_LIBRARY_FILTER_OPTIONS: BomLibraryFilterOptions = {
  componentTypes: [],
  compositions: [],
  finishes: [],
  brands: [],
  subBrands: [],
  suppliers: [],
  createdBy: [],
};

const initialState: BomLibraryV2State = {
  bomTypes: [],
  bomTypesTotal: 0,
  bomTypeSequenceList: [],
  isLoadingBomTypeSequence: false,
  bomLibraries: [],
  productTypes: [],
  bomTypeGroups: [],
  bomTypeGroupsTotal: 0,
  bomLibrariesTotal: 0,
  categories: [],
  catBasedOnPT: false,
  bomLibrarySearchResults: [],
  bomLibraryCards: [],
  bomLibraryCardsTotal: 0,
  bomLibraryDetail: null,
  isLoadingBomLibraryDetail: false,
  bomLibraryTags: [],
  bomLibraryFilterOptions: EMPTY_BOM_LIBRARY_FILTER_OPTIONS,
  bomLibraryGroups: [],
  bomLibraryGroupsTotal: 0,
  seasons: [],
  isLoadingBomTypes: false,
  isLoadingBomLibraries: false,
  isLoadingBomLibraryCards: false,
  isLoadingBomLibraryGroups: false,
  isLoadingBomTypeGroups: false,
  isLoadingCategories: false,
  isSearchingBomLibraries: false,
  error: null,
};

/**
 * Reduces the BOM slice, registered as `BomLibraryV2Details`.
 * @param {BomLibraryV2State} state - Current slice state.
 * @param {BomV2ReducerAction} action - Dispatched action.
 * @returns {BomLibraryV2State} The next slice state.
 */
const bomLibraryV2Reducer = (state: BomLibraryV2State = initialState, action: BomV2ReducerAction): BomLibraryV2State => {
  if (!isBomV2Action(action)) {
    return state;
  }

  switch (action.type) {
    case BOM_V2_ACTION_TYPES.SET_LOADING_BOM_TYPES:
      return { ...state, isLoadingBomTypes: action.payload };
    case BOM_V2_ACTION_TYPES.SET_BOM_TYPES:
      return {
        ...state,
        bomTypes: action.payload.append ? [...state.bomTypes, ...action.payload.items] : action.payload.items,
        bomTypesTotal: action.payload.totalCount,
        isLoadingBomTypes: false,
        error: null,
      };
    case BOM_V2_ACTION_TYPES.SET_LOADING_BOM_TYPE_SEQUENCE:
      return { ...state, isLoadingBomTypeSequence: action.payload };
    case BOM_V2_ACTION_TYPES.SET_BOM_TYPE_SEQUENCE_LIST:
      return { ...state, bomTypeSequenceList: action.payload, isLoadingBomTypeSequence: false, error: null };
    case BOM_V2_ACTION_TYPES.SET_LOADING_BOM_LIBRARIES:
      return { ...state, isLoadingBomLibraries: action.payload };
    case BOM_V2_ACTION_TYPES.SET_BOM_LIBRARIES:
      return {
        ...state,
        bomLibraries: action.payload.append ? [...state.bomLibraries, ...action.payload.items] : action.payload.items,
        bomLibrariesTotal: action.payload.totalCount,
        isLoadingBomLibraries: false,
        error: null,
      };
    case BOM_V2_ACTION_TYPES.SET_PRODUCT_TYPES:
      return { ...state, productTypes: action.payload };
    case BOM_V2_ACTION_TYPES.SET_LOADING_BOM_TYPE_GROUPS:
      return { ...state, isLoadingBomTypeGroups: action.payload };
    case BOM_V2_ACTION_TYPES.SET_BOM_TYPE_GROUPS:
      return {
        ...state,
        bomTypeGroups: action.payload.append ? [...state.bomTypeGroups, ...action.payload.items] : action.payload.items,
        bomTypeGroupsTotal: action.payload.totalCount,
        isLoadingBomTypeGroups: false,
        error: null,
      };
    case BOM_V2_ACTION_TYPES.SET_LOADING_CATEGORIES:
      return { ...state, isLoadingCategories: action.payload };
    case BOM_V2_ACTION_TYPES.SET_CATEGORIES:
      return {
        ...state,
        categories: action.payload.categories,
        catBasedOnPT: action.payload.catBasedOnPT,
        isLoadingCategories: false,
        error: null,
      };
    case BOM_V2_ACTION_TYPES.SET_SEARCHING_BOM_LIBRARIES:
      return { ...state, isSearchingBomLibraries: action.payload };
    case BOM_V2_ACTION_TYPES.SET_BOM_LIBRARY_SEARCH_RESULTS:
      return { ...state, bomLibrarySearchResults: action.payload, isSearchingBomLibraries: false, error: null };
    case BOM_V2_ACTION_TYPES.SET_LOADING_BOM_LIBRARY_CARDS:
      return { ...state, isLoadingBomLibraryCards: action.payload };
    case BOM_V2_ACTION_TYPES.SET_BOM_LIBRARY_CARDS:
      return {
        ...state,
        bomLibraryCards: action.payload.append
          ? [...state.bomLibraryCards, ...action.payload.items]
          : action.payload.items,
        bomLibraryCardsTotal: action.payload.totalCount,
        isLoadingBomLibraryCards: false,
        error: null,
      };
    case BOM_V2_ACTION_TYPES.SET_LOADING_BOM_LIBRARY_DETAIL:
      return { ...state, isLoadingBomLibraryDetail: action.payload };
    case BOM_V2_ACTION_TYPES.SET_BOM_LIBRARY_DETAIL:
      return { ...state, bomLibraryDetail: action.payload };
    case BOM_V2_ACTION_TYPES.SET_BOM_LIBRARY_TAGS:
      return { ...state, bomLibraryTags: action.payload };
    case BOM_V2_ACTION_TYPES.SET_BOM_LIBRARY_FILTER_OPTIONS:
      return { ...state, bomLibraryFilterOptions: action.payload };
    case BOM_V2_ACTION_TYPES.SET_LOADING_BOM_LIBRARY_GROUPS:
      return { ...state, isLoadingBomLibraryGroups: action.payload };
    case BOM_V2_ACTION_TYPES.SET_BOM_LIBRARY_GROUPS:
      return {
        ...state,
        bomLibraryGroups: action.payload.append
          ? [...state.bomLibraryGroups, ...action.payload.items]
          : action.payload.items,
        bomLibraryGroupsTotal: action.payload.totalCount,
        isLoadingBomLibraryGroups: false,
        error: null,
      };
    case BOM_V2_ACTION_TYPES.SET_SEASONS:
      return { ...state, seasons: action.payload };
    case BOM_V2_ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoadingBomTypes: false,
        isLoadingBomTypeSequence: false,
        isLoadingBomLibraries: false,
        isLoadingBomLibraryCards: false,
        isLoadingBomLibraryGroups: false,
        isLoadingBomTypeGroups: false,
        isLoadingCategories: false,
        isSearchingBomLibraries: false,
      };
    default:
      return state;
  }
};

export default bomLibraryV2Reducer;
